import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Popper,
  Fade,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  UploadFile as UploadIcon,
  Close as CloseIcon,
  DriveEta as CarIcon,
  Badge as BadgeIcon,
  Assignment as LicenseIcon,
  Palette as PaletteIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import themes from '../theme/themes';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentTheme, changeTheme, allThemes } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPalette, setOpenPalette] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    gender: 'female',
    role: 'passenger',
    carModel: '',
    carPlate: '',
    carColor: '',
  });

  // الملفات المرفوعة
  const [files, setFiles] = useState({
    carImage: null,
    idImage: null,
    licenseImage: null,
  });
  const [filePreviews, setFilePreviews] = useState({
    carImage: null,
    idImage: null,
    licenseImage: null,
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLanguageChange = (event, newLang) => {
    if (newLang) {
      setLanguage(newLang);
      i18n.changeLanguage(newLang);
    }
  };

  const handlePaletteClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenPalette((prev) => !prev);
  };

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setOpenPalette(false);
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      setFiles({ ...files, [field]: file });
      // معاينة الصورة
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreviews({ ...filePreviews, [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (field) => {
    setFiles({ ...files, [field]: null });
    setFilePreviews({ ...filePreviews, [field]: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // إرسال البيانات النصية
      const response = await axios.post('/api/auth/register', formData);
      
      // إذا كان المستخدم سائقاً، نرفع الملفات
      if (formData.role === 'driver') {
        const formDataWithFiles = new FormData();
        formDataWithFiles.append('userId', response.data.userId);
        
        if (files.carImage) {
          formDataWithFiles.append('carImage', files.carImage);
        }
        if (files.idImage) {
          formDataWithFiles.append('idImage', files.idImage);
        }
        if (files.licenseImage) {
          formDataWithFiles.append('licenseImage', files.licenseImage);
        }

        await axios.post('/api/captains/upload-documents', formDataWithFiles, {
          headers: { 
            'Content-Type': 'multipart/form-data',
          }
        });
      }

      setSuccess(response.data.message || t('registerSuccess'));
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || t('registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 4 }}>
        {/* زر العودة للرئيسية */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Tooltip title={t('home') || 'الرئيسية'}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{
                color: (theme) => theme.palette.primary.main,
                '&:hover': {
                  background: (theme) => `${theme.palette.primary.main}20`,
                },
              }}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>

          {/* زر تبديل الثيمات */}
          <Tooltip title={t('changeTheme') || 'تغيير الثيم'}>
            <IconButton
              onClick={handlePaletteClick}
              sx={{
                color: (theme) => theme.palette.primary.main,
                '&:hover': {
                  background: (theme) => `${theme.palette.primary.main}20`,
                },
              }}
            >
              <PaletteIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* قائمة الثيمات المنبثقة */}
        <Popper
          open={openPalette}
          anchorEl={anchorEl}
          placement="bottom-end"
          transition
          sx={{ zIndex: 9999 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Card
                sx={{
                  p: 2,
                  mt: 1,
                  borderRadius: 3,
                  maxWidth: 280,
                  background: (theme) => theme.palette.background.paper,
                  backdropFilter: 'blur(20px)',
                  border: (theme) => `1px solid ${theme.palette.primary.light}40`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  🎨 اختر الثيم المفضل
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {Object.values(allThemes).map((themeData) => (
                    <Button
                      key={themeData.id}
                      variant={currentTheme === themeData.id ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleThemeChange(themeData.id)}
                      sx={{
                        justifyContent: 'flex-start',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: (theme) => theme.palette.primary.main,
                        color: currentTheme === themeData.id ? '#FFFFFF' : (theme) => theme.palette.text.primary,
                        background: currentTheme === themeData.id 
                          ? (theme) => `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`
                          : 'transparent',
                        '&:hover': {
                          background: currentTheme === themeData.id 
                            ? (theme) => `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`
                            : (theme) => `${themeData.colors.primary}20`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          mr: 1.5,
                          background: `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`,
                          border: '2px solid rgba(255,255,255,0.3)',
                        }}
                      />
                      <span dir="ltr">{themeData.icon} {themeData.name}</span>
                    </Button>
                  ))}
                </Box>
              </Card>
            </Fade>
          )}
        </Popper>

        <Box textAlign="center" mb={3}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              margin: '0 auto',
              background: 'linear-gradient(135deg, #FF1493, #4A00E0)',
            }}
          >
            <PersonAddIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={600} sx={{ mt: 2 }}>
            {t('createAccount') || 'إنشاء حساب جديد'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('joinMessage') || 'انضم إلى خدمة النقل الآمنة للنساء'}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="center" mb={3}>
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderRadius: 50,
                px: 3,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #FF1493, #FF69B4)',
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <ToggleButton value="ar">🇸🇦 العربية</ToggleButton>
            <ToggleButton value="en">🇬🇧 English</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('fullName') || 'الاسم الكامل'}
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
            InputProps={{
              startAdornment: <PersonIcon sx={{ color: '#FF69B4', mr: 1 }} />,
            }}
          />
          <TextField
            fullWidth
            label={t('email') || 'البريد الإلكتروني'}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            required
            InputProps={{
              startAdornment: <EmailIcon sx={{ color: '#FF69B4', mr: 1 }} />,
            }}
          />
          <TextField
            fullWidth
            label={t('password') || 'كلمة المرور'}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            required
            InputProps={{
              startAdornment: <LockIcon sx={{ color: '#FF69B4', mr: 1 }} />,
            }}
          />
          <TextField
            fullWidth
            label={t('phone') || 'رقم الجوال'}
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            margin="normal"
            InputProps={{
              startAdornment: <PhoneIcon sx={{ color: '#FF69B4', mr: 1 }} />,
            }}
          />

          <Box display="flex" gap={2} mt={2}>
            <TextField
              fullWidth
              select
              label={t('role') || 'الدور'}
              name="role"
              value={formData.role}
              onChange={handleChange}
            >
              <MenuItem value="passenger">{t('passenger') || 'راكبة'}</MenuItem>
              <MenuItem value="driver">{t('captain') || 'سائقة'}</MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              label={t('gender') || 'الجنس'}
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <MenuItem value="female">{t('female') || 'أنثى'}</MenuItem>
              <MenuItem value="male">{t('male') || 'ذكر'}</MenuItem>
            </TextField>
          </Box>

          {formData.role === 'driver' && (
            <Box mt={3}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#FF1493' }}>
                🚗 {t('carInfo') || 'معلومات السيارة'}
              </Typography>
              
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: '#fafafa' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('carModel') || 'موديل السيارة'}
                      name="carModel"
                      value={formData.carModel}
                      onChange={handleChange}
                      margin="dense"
                      InputProps={{
                        startAdornment: <CarIcon sx={{ color: '#FF1493', mr: 1 }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('carPlate') || 'رقم اللوحة'}
                      name="carPlate"
                      value={formData.carPlate}
                      onChange={handleChange}
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('carColor') || 'لون السيارة'}
                      name="carColor"
                      value={formData.carColor}
                      onChange={handleChange}
                      margin="dense"
                    />
                  </Grid>
                </Grid>

                {/* رفع صورة السيارة */}
                <Box mt={2}>
                  <Typography variant="body2" fontWeight={600}>
                    🖼️ {t('carImage') || 'صورة السيارة'}
                  </Typography>
                  {filePreviews.carImage ? (
                    <Box position="relative" mt={1}>
                      <img 
                        src={filePreviews.carImage} 
                        alt="السيارة" 
                        style={{ 
                          width: '100%', 
                          maxHeight: 150, 
                          objectFit: 'cover', 
                          borderRadius: 8,
                          border: '1px solid #ddd'
                        }} 
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={() => removeFile('carImage')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ mt: 1, py: 2, borderStyle: 'dashed', borderRadius: 2 }}
                    >
                      <UploadIcon sx={{ mr: 1 }} />
                      {t('uploadCarImage') || 'رفع صورة السيارة'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleFileChange(e, 'carImage')}
                      />
                    </Button>
                  )}
                </Box>
              </Card>

              <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 2, color: '#4A00E0' }}>
                🆔 {t('identityDocuments') || 'وثائق الهوية'}
              </Typography>

              <Card sx={{ p: 2, borderRadius: 3, bgcolor: '#fafafa' }}>
                {/* الهوية الوطنية */}
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    🪪 {t('idImage') || 'صورة الهوية الوطنية / الإقامة'}
                  </Typography>
                  {filePreviews.idImage ? (
                    <Box position="relative" mt={1}>
                      <img 
                        src={filePreviews.idImage} 
                        alt="الهوية" 
                        style={{ 
                          width: '100%', 
                          maxHeight: 150, 
                          objectFit: 'cover', 
                          borderRadius: 8,
                          border: '1px solid #ddd'
                        }} 
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={() => removeFile('idImage')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ mt: 1, py: 2, borderStyle: 'dashed', borderRadius: 2 }}
                    >
                      <BadgeIcon sx={{ mr: 1 }} />
                      {t('uploadIdImage') || 'رفع صورة الهوية'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleFileChange(e, 'idImage')}
                      />
                    </Button>
                  )}
                </Box>

                {/* رخصة القيادة */}
                <Box mt={2}>
                  <Typography variant="body2" fontWeight={600}>
                    📜 {t('licenseImage') || 'صورة رخصة القيادة'}
                  </Typography>
                  {filePreviews.licenseImage ? (
                    <Box position="relative" mt={1}>
                      <img 
                        src={filePreviews.licenseImage} 
                        alt="رخصة القيادة" 
                        style={{ 
                          width: '100%', 
                          maxHeight: 150, 
                          objectFit: 'cover', 
                          borderRadius: 8,
                          border: '1px solid #ddd'
                        }} 
                      />
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.8)' }}
                        onClick={() => removeFile('licenseImage')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ mt: 1, py: 2, borderStyle: 'dashed', borderRadius: 2 }}
                    >
                      <LicenseIcon sx={{ mr: 1 }} />
                      {t('uploadLicenseImage') || 'رفع صورة الرخصة'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleFileChange(e, 'licenseImage')}
                      />
                    </Button>
                  )}
                </Box>
              </Card>

              <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
                {t('documentsNote') || 'سيتم مراجعة المستندات من قبل الإدارة للموافقة على طلبك'}
              </Alert>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 3 }}>
              {success}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.5,
              borderRadius: 50,
              fontSize: 16,
              background: 'linear-gradient(135deg, #FF1493, #FF69B4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #C71585, #FF1493)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : (t('register') || 'إنشاء حساب')}
          </Button>

          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              {t('haveAccount') || 'لديك حساب؟'}{' '}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/login')}
                sx={{
                  textTransform: 'none',
                  color: '#FF1493',
                  '&:hover': {
                    background: 'rgba(255, 20, 147, 0.08)',
                  },
                }}
              >
                {t('login') || 'تسجيل الدخول'}
              </Button>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default RegisterPage;